import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useModuleAccess } from '../hooks/useModuleAccess';
import SubscriptionGate from '../components/common/SubscriptionGate';
import { toast } from 'sonner';
import {
  Lightbulb,
  Users,
  MapPin,
  Clock,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  RefreshCw,
  BookOpen,
  MessageSquare,
  PenTool,
  FileText,
  Info
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Nautical grades - surveyors can come from any MMD
const NAUTICAL_GRADES = ['Master', 'Chief Mate', '2MFG'];
const isNauticalGrade = (grade) => NAUTICAL_GRADES.includes(grade);

const InsightsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { preferences } = usePreferences();
  const { canAccessExamInsights, loading: accessLoading, isPaid } = useSubscriptionAccess();
  const { isInsightsActiveForGrade, getInsightsStatusForGrade, isModuleHidden, loading: moduleLoading } = useModuleAccess();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('questions');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [topicsByFunction, setTopicsByFunction] = useState([]);
  const [examType, setExamType] = useState('oral');
  const [questionsDays, setQuestionsDays] = useState('all');
  const [surveyors, setSurveyors] = useState({ internal: [], external: [], isNautical: false, mmdGroups: null });
  const [selectedSurveyor, setSelectedSurveyor] = useState(null);
  const [surveyorTopics, setSurveyorTopics] = useState(null);
  const [functionFilter, setFunctionFilter] = useState('all');
  const [surveyorDays, setSurveyorDays] = useState('all');
  const [functions, setFunctions] = useState([]);
  const [mmdInsights, setMmdInsights] = useState(null);
  const [mmdFunctionFilter, setMmdFunctionFilter] = useState('all');
  const [mmdDays, setMmdDays] = useState('all');
  const [recentInsights, setRecentInsights] = useState(null);
  const [recentDays, setRecentDays] = useState('30');
  const [expandedFunctions, setExpandedFunctions] = useState({});
  
  const grade = preferences?.grade || 'MEO Class 2';
  const mmdLocation = preferences?.mmd_location || 'Mumbai';

  // Check if insights is active for the user's grade
  const isInsightsAvailable = isInsightsActiveForGrade(grade);
  const insightsStatus = getInsightsStatusForGrade(grade);

  const fetchOverview = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/insights/overview?grade=${encodeURIComponent(grade)}&mmd_location=${encodeURIComponent(mmdLocation)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) setOverview(await response.json());
    } catch (error) {
      console.error('Failed to fetch overview:', error);
    }
  }, [grade, mmdLocation]);

  const fetchTopicsByFunction = useCallback(async (type = examType, days = questionsDays) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/insights/topics-by-function?grade=${encodeURIComponent(grade)}&exam_type=${type}`;
      if (days && days !== 'all') {
        url += `&days=${days}`;
      }
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setTopicsByFunction(data.data || []);
        // Expand first function by default
        if (data.data?.length > 0) {
          setExpandedFunctions({ [data.data[0].function_id]: true });
        }
      }
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  }, [grade]);

  const fetchSurveyors = useCallback(async (funcId = null, days = surveyorDays) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/insights/surveyors-at-mmd?grade=${encodeURIComponent(grade)}&mmd_location=${encodeURIComponent(mmdLocation)}`;
      if (funcId && funcId !== 'all') {
        url += `&function_id=${encodeURIComponent(funcId)}`;
      }
      if (days && days !== 'all') {
        url += `&days=${days}`;
      }
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setSurveyors({ 
          internal: data.internal || [], 
          external: data.external || [],
          isNautical: data.is_nautical || false,
          mmdGroups: data.mmd_groups || null
        });
      }
    } catch (error) {
      console.error('Failed to fetch surveyors:', error);
    }
  }, [grade, mmdLocation]);

  const fetchFunctions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/insights/functions?grade=${encodeURIComponent(grade)}&exam_type=oral`,
        { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setFunctions((await response.json()).data || []);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
    }
  }, [grade]);

  const fetchMmdInsights = useCallback(async (funcId = null, days = mmdDays) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/insights/your-mmd?grade=${encodeURIComponent(grade)}&mmd_location=${encodeURIComponent(mmdLocation)}`;
      if (funcId && funcId !== 'all') {
        url += `&function_id=${encodeURIComponent(funcId)}`;
      }
      if (days && days !== 'all') {
        url += `&days=${days}`;
      }
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setMmdInsights(await response.json());
    } catch (error) {
      console.error('Failed to fetch MMD insights:', error);
    }
  }, [grade, mmdLocation]);

  const fetchRecentInsights = useCallback(async (days = recentDays) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/insights/recent?grade=${encodeURIComponent(grade)}&days=${days}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) setRecentInsights(await response.json());
    } catch (error) {
      console.error('Failed to fetch recent insights:', error);
    }
  }, [grade]);

  const fetchSurveyorTopics = async (surveyorId, surveyorName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/insights/surveyor-topics/${surveyorId}?grade=${encodeURIComponent(grade)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSurveyorTopics(data);
        setSelectedSurveyor({ id: surveyorId, name: surveyorName });
      }
    } catch (error) {
      console.error('Failed to fetch surveyor topics:', error);
    }
  };

  const fetchAllData = useCallback(async (showRefresh = false) => {
    if (!user) return;
    
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      await Promise.all([
        fetchOverview(),
        fetchTopicsByFunction(examType, questionsDays),
        fetchSurveyors(functionFilter, surveyorDays),
        fetchFunctions(),
        fetchMmdInsights(mmdFunctionFilter, mmdDays),
        fetchRecentInsights(recentDays)
      ]);

      if (showRefresh) toast.success('Data refreshed');
    } catch (error) {
      console.error('Failed to fetch insights:', error);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, fetchOverview, fetchTopicsByFunction, fetchSurveyors, fetchFunctions, fetchMmdInsights, fetchRecentInsights, examType, questionsDays, functionFilter, surveyorDays, mmdFunctionFilter, mmdDays, recentDays]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Handle Questions tab filter changes
  const handleQuestionsFilterChange = (type, days) => {
    if (type !== undefined) setExamType(type);
    if (days !== undefined) setQuestionsDays(days);
    fetchTopicsByFunction(type || examType, days || questionsDays);
  };

  // Handle Surveyors tab filter changes
  const handleSurveyorsFilterChange = (funcId, days) => {
    if (funcId !== undefined) setFunctionFilter(funcId);
    if (days !== undefined) setSurveyorDays(days);
    fetchSurveyors(funcId || functionFilter, days || surveyorDays);
  };

  // Handle MMD tab filter changes
  const handleMmdFilterChange = (funcId, days) => {
    if (funcId !== undefined) setMmdFunctionFilter(funcId);
    if (days !== undefined) setMmdDays(days);
    fetchMmdInsights(funcId || mmdFunctionFilter, days || mmdDays);
  };

  // Handle Recent days filter change
  const handleDaysChange = (days) => {
    setRecentDays(days);
    fetchRecentInsights(days);
  };

  const toggleFunction = (funcId) => {
    setExpandedFunctions(prev => ({
      ...prev,
      [funcId]: !prev[funcId]
    }));
  };

  // Check subscription access
  const hasAccess = canAccessExamInsights();

  if (loading || accessLoading || moduleLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
            <p className="text-gray-500">Loading exam insights...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Check if insights is available for this grade (module activation check)
  // Grade-only gate — Exam Insights are grade-scoped but not MMD-scoped.
  if (!preferences?.grade) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto text-center py-16">
          <Lightbulb className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Grade Required</h2>
          <p className="text-gray-500 mb-6">
            Please select your grade in your profile to access Exam Insights.
          </p>
          <Button onClick={() => navigate('/profile')} className="bg-[#0891B2] hover:bg-[#0891B2]/90">
            Go to Profile
          </Button>
        </div>
      </Layout>
    );
  }
  
  if (!isInsightsAvailable) {
    return (
      <Layout>
        <div className="page-transition space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Lightbulb className="w-7 h-7 text-gray-400" />
                Exam Insights
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {grade} • {mmdLocation}
              </p>
            </div>
          </div>
          
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {insightsStatus === 'coming_soon' ? 'Coming Soon' : 'Not Available'}
              </h2>
              <p className="text-gray-600 mb-4">
                {insightsStatus === 'coming_soon' 
                  ? `Exam Insights for ${grade} is currently being prepared. We're analyzing exam patterns to bring you valuable insights.`
                  : `Exam Insights is not available for ${grade} at this time.`
                }
              </p>
              <p className="text-sm text-gray-500">
                Check back soon or explore other available features.
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate('/question-bank')}
              >
                Explore Question Bank
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Gate the content for non-subscribed users
  if (!hasAccess) {
    return (
      <Layout>
        <div className="page-transition space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Lightbulb className="w-7 h-7 text-teal-600" />
                Exam Insights
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {preferences?.grade || 'Select Grade'} • {preferences?.mmd_location || 'Select MMD'}
              </p>
            </div>
          </div>
          
          <SubscriptionGate
            hasAccess={false}
            featureName="Exam Insights"
            moduleKey="exam_insights"
            description="Unlock detailed exam trends, topic analytics, surveyor patterns, and MMD-specific insights to supercharge your preparation strategy."
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Lightbulb className="w-7 h-7 text-teal-600" />
                Exam Insights
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {grade} • {mmdLocation}
              </p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchAllData(true)}
            disabled={refreshing}
            data-testid="refresh-insights-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Overview Stats - Grade Specific */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="stat-oral-questions">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{overview?.oral_questions || 0}</p>
                  <p className="text-xs text-gray-500">Oral Questions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-written-questions">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <PenTool className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{overview?.written_questions || 0}</p>
                  <p className="text-xs text-gray-500">Written Questions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-active-surveyors">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{overview?.active_surveyors || 0}</p>
                  <p className="text-xs text-gray-500">Active Surveyors</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-topics">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {overview?.oral_topics || 0}<span className="text-gray-400 mx-1">/</span>{overview?.written_topics || 0}
                  </p>
                  <p className="text-xs text-gray-500">Topics (Oral/Written)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="questions" data-testid="tab-questions">
              <FileText className="w-4 h-4 mr-2 hidden sm:block" />
              Questions
            </TabsTrigger>
            <TabsTrigger value="surveyors" data-testid="tab-surveyors">
              <Users className="w-4 h-4 mr-2 hidden sm:block" />
              Surveyors
            </TabsTrigger>
            <TabsTrigger value="mmd" data-testid="tab-mmd">
              <MapPin className="w-4 h-4 mr-2 hidden sm:block" />
              Your MMD
            </TabsTrigger>
            <TabsTrigger value="recent" data-testid="tab-recent">
              <Clock className="w-4 h-4 mr-2 hidden sm:block" />
              Recent
            </TabsTrigger>
          </TabsList>

          {/* Questions Tab */}
          <TabsContent value="questions" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">Topic-wise Question Distribution</CardTitle>
                    <CardDescription>Questions grouped by function and topic</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={questionsDays} onValueChange={(d) => handleQuestionsFilterChange(undefined, d)}>
                      <SelectTrigger className="w-[140px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="60">Last 60 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                      </SelectContent>
                    </Select>
                    <Tabs value={examType} onValueChange={(t) => handleQuestionsFilterChange(t, undefined)} className="w-auto">
                      <TabsList className="h-8">
                        <TabsTrigger value="oral" className="text-xs px-3 h-7" data-testid="exam-type-oral">
                          Oral
                        </TabsTrigger>
                        <TabsTrigger value="written" className="text-xs px-3 h-7" data-testid="exam-type-written">
                          Written
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {topicsByFunction.length > 0 ? (
                  <div className="space-y-2">
                    {topicsByFunction.map((func) => (
                      <Collapsible
                        key={func.function_id}
                        open={expandedFunctions[func.function_id]}
                        onOpenChange={() => toggleFunction(func.function_id)}
                      >
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                            <div className="flex items-center gap-3">
                              {expandedFunctions[func.function_id] ? (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-500" />
                              )}
                              <span className="font-medium text-gray-800">{func.function_name}</span>
                            </div>
                            <Badge variant="secondary" className="bg-teal-100 text-teal-700">
                              {func.total_questions} questions
                            </Badge>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="ml-7 mt-2 space-y-1 pb-2">
                            {func.topics.map((topic, idx) => (
                              <div
                                key={topic.topic_id}
                                className="flex items-center justify-between gap-3 py-2 px-3 hover:bg-gray-50 rounded"
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600 flex-shrink-0">
                                    {idx + 1}
                                  </span>
                                  <span className="text-sm text-gray-700 break-words min-w-0">{topic.topic_name}</span>
                                </div>
                                <span className="text-sm font-medium text-gray-600 flex-shrink-0">{topic.count} Q</span>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-gray-400">
                    No {examType} questions found for {grade}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Surveyors Tab */}
          <TabsContent value="surveyors" className="space-y-4">
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Surveyor List */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-3">
                    <div>
                      <CardTitle className="text-lg">
                        {surveyors.isNautical 
                          ? `All Surveyors for ${grade}` 
                          : `Active Surveyors at ${mmdLocation}`}
                      </CardTitle>
                      <CardDescription>
                        {surveyors.isNautical 
                          ? 'Nautical exams can have surveyors from any MMD - grouped by their home location'
                          : 'Click to see their topic preferences'}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={surveyorDays} onValueChange={(d) => handleSurveyorsFilterChange(undefined, d)}>
                        <SelectTrigger className="w-[140px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Time</SelectItem>
                          <SelectItem value="30">Last 30 days</SelectItem>
                          <SelectItem value="60">Last 60 days</SelectItem>
                          <SelectItem value="90">Last 90 days</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={functionFilter} onValueChange={(f) => handleSurveyorsFilterChange(f, undefined)}>
                        <SelectTrigger className="w-[160px] h-8 text-xs">
                          <SelectValue placeholder="All Functions" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Functions</SelectItem>
                          {functions.map(f => (
                            <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {(surveyors.internal?.length > 0 || surveyors.external?.length > 0) ? (
                    <div className="space-y-4 max-h-[400px] overflow-y-auto">
                      {/* For Nautical grades: Group by MMD */}
                      {surveyors.isNautical && surveyors.mmdGroups ? (
                        Object.entries(surveyors.mmdGroups).map(([mmd, group]) => (
                          <Collapsible key={mmd} defaultOpen={true}>
                            <CollapsibleTrigger className="w-full">
                              <div className="flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-purple-500" />
                                  <span className="font-medium text-sm text-gray-700">{mmd}</span>
                                </div>
                                <Badge variant="secondary" className="text-xs">
                                  {(group.internal?.length || 0) + (group.external?.length || 0)} surveyors
                                </Badge>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-2 ml-6 space-y-3">
                              {/* Internal Surveyors at this MMD */}
                              {group.internal?.length > 0 && (
                                <div>
                                  <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Internal</h5>
                                  <div className="space-y-1">
                                    {group.internal.map((surveyor) => (
                                      <div
                                        key={surveyor.surveyor_id}
                                        className={`p-2 rounded-lg border cursor-pointer transition-all hover:border-teal-300 hover:bg-teal-50 ${
                                          selectedSurveyor?.id === surveyor.surveyor_id ? 'border-teal-500 bg-teal-50' : ''
                                        }`}
                                        onClick={() => fetchSurveyorTopics(surveyor.surveyor_id, surveyor.surveyor_name)}
                                        data-testid={`surveyor-${surveyor.surveyor_id}`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <Users className="w-3 h-3 text-gray-400" />
                                            <span className="font-medium text-sm">{surveyor.surveyor_name}</span>
                                          </div>
                                          <ChevronRight className="w-3 h-3 text-gray-400" />
                                        </div>
                                        {surveyor.question_count > 0 && (
                                          <div className="mt-1 text-xs text-gray-500">
                                            {surveyor.question_count} Q • {surveyor.topic_count} topics
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {/* External Surveyors at this MMD */}
                              {group.external?.length > 0 && (
                                <div>
                                  <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">External</h5>
                                  <div className="space-y-1">
                                    {group.external.map((surveyor) => (
                                      <div
                                        key={surveyor.surveyor_id}
                                        className={`p-2 rounded-lg border cursor-pointer transition-all hover:border-teal-300 hover:bg-teal-50 ${
                                          selectedSurveyor?.id === surveyor.surveyor_id ? 'border-teal-500 bg-teal-50' : ''
                                        }`}
                                        onClick={() => fetchSurveyorTopics(surveyor.surveyor_id, surveyor.surveyor_name)}
                                        data-testid={`surveyor-${surveyor.surveyor_id}`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <Users className="w-3 h-3 text-gray-400" />
                                            <span className="font-medium text-sm">{surveyor.surveyor_name}</span>
                                          </div>
                                          <ChevronRight className="w-3 h-3 text-gray-400" />
                                        </div>
                                        {surveyor.question_count > 0 && (
                                          <div className="mt-1 text-xs text-gray-500">
                                            {surveyor.question_count} Q • {surveyor.topic_count} topics
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </CollapsibleContent>
                          </Collapsible>
                        ))
                      ) : (
                        /* For Engineering grades: Flat list by type */
                        <>
                          {/* Internal Surveyors */}
                          {surveyors.internal?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Internal Surveyors</h4>
                              <div className="space-y-2">
                                {surveyors.internal.map((surveyor) => (
                                  <div
                                    key={surveyor.surveyor_id}
                                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:border-teal-300 hover:bg-teal-50 ${
                                      selectedSurveyor?.id === surveyor.surveyor_id ? 'border-teal-500 bg-teal-50' : ''
                                    }`}
                                    onClick={() => fetchSurveyorTopics(surveyor.surveyor_id, surveyor.surveyor_name)}
                                    data-testid={`surveyor-${surveyor.surveyor_id}`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium text-sm">{surveyor.surveyor_name}</span>
                                      </div>
                                      <ChevronRight className="w-4 h-4 text-gray-400" />
                                    </div>
                                    {surveyor.question_count > 0 && (
                                      <div className="mt-2 text-xs text-gray-500">
                                        {surveyor.question_count} questions • {surveyor.topic_count} topics
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* External Surveyors */}
                          {surveyors.external?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">External Surveyors</h4>
                              <div className="space-y-2">
                                {surveyors.external.map((surveyor) => (
                                  <div
                                    key={surveyor.surveyor_id}
                                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:border-teal-300 hover:bg-teal-50 ${
                                      selectedSurveyor?.id === surveyor.surveyor_id ? 'border-teal-500 bg-teal-50' : ''
                                    }`}
                                    onClick={() => fetchSurveyorTopics(surveyor.surveyor_id, surveyor.surveyor_name)}
                                    data-testid={`surveyor-${surveyor.surveyor_id}`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium text-sm">{surveyor.surveyor_name}</span>
                                      </div>
                                      <ChevronRight className="w-4 h-4 text-gray-400" />
                                    </div>
                                    {surveyor.question_count > 0 && (
                                      <div className="mt-2 text-xs text-gray-500">
                                        {surveyor.question_count} questions • {surveyor.topic_count} topics
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="py-12 text-center text-gray-400">
                      {surveyors.isNautical 
                        ? `No active surveyors found for ${grade}`
                        : `No active surveyors found at ${mmdLocation}`}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Surveyor Topic Breakdown */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">
                    {selectedSurveyor ? `${selectedSurveyor.name}'s Topics` : 'Select a Surveyor'}
                  </CardTitle>
                  <CardDescription>
                    {selectedSurveyor 
                      ? `${surveyorTopics?.total_questions || 0} questions in database`
                      : 'Click a surveyor to see what they ask'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {surveyorTopics && (surveyorTopics.functions?.length > 0 || surveyorTopics.uncategorized_questions > 0) ? (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {surveyorTopics.functions.map((func) => (
                        <div key={func.function_id} className="border rounded-lg p-3">
                          <h4 className="font-medium text-sm text-gray-700 mb-2">{func.function_name}</h4>
                          <div className="space-y-1">
                            {func.topics.map((topic) => (
                              <div
                                key={topic.topic_id}
                                className="flex items-start justify-between gap-2 text-sm py-1"
                              >
                                <span className="text-gray-600 break-words min-w-0 flex-1">{topic.topic_name}</span>
                                <Badge variant="secondary" className="text-xs flex-shrink-0">
                                  {topic.count}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {/* Show uncategorized questions if any */}
                      {surveyorTopics.uncategorized_questions > 0 && (
                        <div className="border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 italic">Uncategorized Questions</span>
                            <Badge variant="outline" className="text-xs text-gray-500">
                              {surveyorTopics.uncategorized_questions}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            These questions have not been assigned to a specific topic yet
                          </p>
                        </div>
                      )}
                    </div>
                  ) : selectedSurveyor ? (
                    <div className="py-12 text-center text-gray-400">
                      No questions from this surveyor yet
                    </div>
                  ) : (
                    <div className="py-12 text-center text-gray-400">
                      Select a surveyor from the left
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Your MMD Tab */}
          <TabsContent value="mmd" className="space-y-4">
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Hot Topics */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-3">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-purple-600" />
                        Hot Topics at {mmdLocation}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-4 h-4 text-gray-400 hover:text-purple-600 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[250px] p-3">
                              <p className="font-medium mb-1">How topics are ranked:</p>
                              <ul className="text-xs space-y-0.5">
                                <li>• Question Count (60%)</li>
                                <li>• Unique Surveyors (30%)</li>
                                <li>• Recency (10%)</li>
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </CardTitle>
                      <CardDescription>
                        Oral questions • {mmdInsights?.total_questions || 0} total
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={mmdDays} onValueChange={(d) => handleMmdFilterChange(undefined, d)}>
                        <SelectTrigger className="w-[140px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Time</SelectItem>
                          <SelectItem value="30">Last 30 days</SelectItem>
                          <SelectItem value="60">Last 60 days</SelectItem>
                          <SelectItem value="90">Last 90 days</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={mmdFunctionFilter} onValueChange={(f) => handleMmdFilterChange(f, undefined)}>
                        <SelectTrigger className="w-[160px] h-8 text-xs">
                          <SelectValue placeholder="All Functions" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Functions</SelectItem>
                          {functions.map(f => (
                            <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Info text about ranking */}
                  <div className="mb-4 p-3 bg-purple-50 rounded-lg text-xs text-purple-700">
                    <p className="font-medium mb-1">How topics are ranked:</p>
                    <ul className="space-y-0.5 text-purple-600">
                      <li>• Question count (60%)</li>
                      <li>• Unique surveyors (30%)</li>
                      <li>• Recency bonus (10%)</li>
                    </ul>
                  </div>
                  {mmdInsights?.hot_topics?.length > 0 ? (
                    <div className="space-y-2">
                      {mmdInsights.hot_topics.map((topic, idx) => (
                        <div
                          key={topic.topic_id}
                          className="flex items-start justify-between gap-3 p-2 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <span
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0 ${
                                idx < 3 ? 'bg-purple-500' : 'bg-gray-400'
                              }`}
                            >
                              {idx + 1}
                            </span>
                            <div className="flex flex-col min-w-0 flex-1">
                              <span className="text-sm break-words">{topic.topic_name}</span>
                              <span className="text-xs text-gray-400">
                                {topic.unique_surveyors || 0} surveyor{(topic.unique_surveyors || 0) !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
                            {topic.is_recent && (
                              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">Recent</Badge>
                            )}
                            <Badge variant="outline" className="text-xs bg-teal-50 text-teal-700 border-teal-200">Oral</Badge>
                            <Badge variant="outline">{topic.count} Q</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-12 text-center text-gray-400">
                      No data available for {mmdLocation}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Active Surveyors at MMD */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Active Surveyors</CardTitle>
                  <CardDescription>
                    Currently examining at {mmdLocation}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {(mmdInsights?.internal_surveyors?.length > 0 || mmdInsights?.external_surveyors?.length > 0) ? (
                    <div className="space-y-4">
                      {/* Internal Surveyors */}
                      {mmdInsights?.internal_surveyors?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Internal</h4>
                          <div className="space-y-1">
                            {mmdInsights.internal_surveyors.map((surveyor) => (
                              <div
                                key={surveyor.surveyor_id}
                                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50"
                              >
                                <Users className="w-4 h-4 text-gray-400" />
                                <span className="text-sm">{surveyor.surveyor_name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* External Surveyors */}
                      {mmdInsights?.external_surveyors?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">External</h4>
                          <div className="space-y-1">
                            {mmdInsights.external_surveyors.map((surveyor) => (
                              <div
                                key={surveyor.surveyor_id}
                                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50"
                              >
                                <Users className="w-4 h-4 text-gray-400" />
                                <span className="text-sm">{surveyor.surveyor_name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="py-12 text-center text-gray-400">
                      No active surveyors at {mmdLocation}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent from MMD */}
            {mmdInsights?.recent_questions > 0 && (
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <Clock className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-purple-800">
                        {mmdInsights.recent_questions} new questions added
                      </p>
                      <p className="text-sm text-purple-600">From {mmdLocation} in the last 90 days</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Recent Tab */}
          <TabsContent value="recent" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Recently Added</CardTitle>
                    <CardDescription>New questions and topics</CardDescription>
                  </div>
                  <Select value={recentDays} onValueChange={handleDaysChange}>
                    <SelectTrigger className="w-[150px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="60">Last 60 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {/* Info note about bulk imports */}
                <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700">
                    This data includes all approved questions, including those from bulk imports.
                  </p>
                </div>
                {recentInsights ? (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-teal-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-teal-700">{recentInsights.new_oral_questions}</p>
                        <p className="text-xs text-teal-600">New Oral</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-700">{recentInsights.new_written_questions}</p>
                        <p className="text-xs text-purple-600">New Written</p>
                      </div>
                      <div className="p-4 bg-gray-100 rounded-lg text-center">
                        <p className="text-2xl font-bold text-gray-700">{recentInsights.total_new}</p>
                        <p className="text-xs text-gray-600">Total New</p>
                      </div>
                    </div>

                    {/* New by Topic */}
                    {recentInsights.new_by_topic?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-700 mb-3">New Questions by Topic</h4>
                        <div className="space-y-2">
                          {recentInsights.new_by_topic.map((topic, idx) => (
                            <div
                              key={topic.topic_id}
                              className="flex items-start justify-between gap-2 py-2 px-3 bg-gray-50 rounded-lg"
                            >
                              <span className="text-sm text-gray-700 break-words min-w-0 flex-1">{topic.topic_name}</span>
                              <Badge className="bg-teal-100 text-teal-700 flex-shrink-0">+{topic.count}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recently Updated Topics */}
                    {recentInsights.recently_updated_topics?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-700 mb-3">Recently Updated Topics</h4>
                        <div className="flex flex-wrap gap-2">
                          {recentInsights.recently_updated_topics.map((topic) => (
                            <Badge key={topic.topic_id} variant="outline" className="py-1 max-w-full whitespace-normal break-words text-left leading-snug">
                              {topic.topic_name}
                              <span className="ml-1 text-teal-600">+{topic.new_questions}</span>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {recentInsights.total_new === 0 && (
                      <div className="py-8 text-center text-gray-400">
                        No new questions added in the last {recentDays} days
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-12 text-center text-gray-400">
                    Loading recent data...
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default InsightsPage;
